import type { FieldRef } from '@contember/bindx'
import { extractFieldName } from '@contember/bindx-dataview'
import { CheckIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import type { CourseTermStatus, GdCallStatus, GdOrderStatus, OrderStatus } from '../admin-schema'
import { isTermConcluded, type TermDateLike } from '../lib/term-dates'
import { gdCallStatusLabels, gdOrderStatusLabels } from './gd-labels'

type StatusStyle = { label: string; tone: string }

const orderStyles: Record<OrderStatus, StatusStyle> = {
	new: { label: 'Nová', tone: 'bg-blue-primary/10 text-blue-primary' },
	awaitingPayment: { label: 'Čeká na platbu', tone: 'bg-warning-bg text-warning' },
	paid: { label: 'Zaplaceno', tone: 'bg-success-bg text-success' },
	substitute: { label: 'Náhradník', tone: 'bg-light-blue/15 text-light-blue' },
	cancelled: { label: 'Zrušeno', tone: 'bg-light-gray text-gray' },
}

/**
 * The term's colours, shared with the status picker on the term form: the chip an editor clicks
 * to set the state is the same green/red/grey the listing shows for it afterwards.
 */
export const termStatusTone: Record<CourseTermStatus, string> = {
	upcoming: 'bg-success-bg text-success',
	cancelled: 'bg-danger-bg text-danger',
	past: 'bg-light-gray text-gray',
}

const termStyles: Record<CourseTermStatus, StatusStyle> = {
	upcoming: { label: 'Nadcházející', tone: termStatusTone.upcoming },
	cancelled: { label: 'Zrušený', tone: termStatusTone.cancelled },
	past: { label: 'Proběhlý', tone: termStatusTone.past },
}

/** Same shape as the publication pills below — a soft rectangle, no outline. */
function StatusPill({ style }: { style: StatusStyle }) {
	return (
		<span className={'inline-flex items-center whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ' + style.tone}>
			{style.label}
		</span>
	)
}

export function OrderStatusBadge({ value }: { value: OrderStatus | null | undefined }) {
	if (!value) return <span className="text-gray">—</span>
	return <StatusPill style={orderStyles[value] ?? orderStyles.new} />
}

/**
 * Hand it the term's dates and a term whose last day has passed reads
 * „Proběhlý" even while the stored status still says `upcoming` — the daily
 * cron only catches up the next morning. See {@link isTermConcluded}.
 */
export function TermStatusBadge({ value, dates }: { value: CourseTermStatus | null | undefined; dates?: readonly TermDateLike[] }) {
	if (!value) return <span className="text-gray">—</span>
	const effective = value === 'upcoming' && dates && isTermConcluded(dates) ? 'past' : value
	return <StatusPill style={termStyles[effective] ?? termStyles.upcoming} />
}

type BadgeTone = 'neutral' | 'info' | 'accent' | 'warning' | 'success' | 'danger' | 'rose'

const toneClasses: Record<BadgeTone, string> = {
	neutral: 'bg-light-gray text-gray ring-gray/30',
	info: 'bg-blue-primary/10 text-blue-primary ring-blue-primary/30',
	/** The brand salmon — for the value someone set here, as opposed to one that came along. */
	accent: 'bg-orange/10 text-orange ring-orange/30',
	warning: 'bg-warning-bg text-warning ring-warning/30',
	/** Doběhlý stav, který není chyba — „Vypršelo" u GD předplatného. Oranžová je v adminu
	    varování („Čeká na platbu"), a tohle na nic nečeká; růžová to od sebe odliší. */
	rose: 'bg-rose-100 text-rose-700 ring-rose-300',
	success: 'bg-success-bg text-success ring-success/30',
	danger: 'bg-danger-bg text-danger ring-danger/30',
}

export function Badge({
	tone = 'neutral',
	shape = 'soft',
	children,
	className,
}: {
	tone?: BadgeTone
	/**
	 * `soft` — the flat, barely rounded rectangle the status pills use, and what
	 * every badge in the admin now looks like. The outlined `pill` is opt-in: in a
	 * grid row it read as a button next to a clickable title, and the ring drew a
	 * second edge around a colour that already said enough.
	 */
	shape?: 'pill' | 'soft'
	children: ReactNode
	className?: string
}) {
	return (
		<span
			className={'inline-flex items-center whitespace-nowrap text-xs font-medium '
				+ (shape === 'pill' ? 'rounded-full px-2.5 py-0.5 ring-1 ring-inset ' : 'rounded px-2 py-0.5 ')
				+ toneClasses[tone]
				+ (className ? ' ' + className : '')}
		>
			{children}
		</span>
	)
}

const gdCallStatusTones: Record<GdCallStatus, BadgeTone> = {
	draft: 'neutral',
	pendingApproval: 'warning',
	published: 'success',
}

const gdOrderStatusTones: Record<GdOrderStatus, BadgeTone> = {
	new: 'info',
	awaitingPayment: 'warning',
	paid: 'success',
	cancelled: 'neutral',
	expired: 'danger',
}

export function GdCallStatusBadge({ value }: { value: GdCallStatus | null | undefined }) {
	if (!value) return <span className="text-gray">—</span>
	return <Badge tone={gdCallStatusTones[value] ?? 'neutral'}>{gdCallStatusLabels[value] ?? value}</Badge>
}

export function GdOrderStatusBadge({ value }: { value: GdOrderStatus | null | undefined }) {
	if (!value) return <span className="text-gray">—</span>
	return <Badge tone={gdOrderStatusTones[value] ?? 'neutral'}>{gdOrderStatusLabels[value] ?? value}</Badge>
}

/**
 * One palette for the publication states wherever they show up — the pill in a grid row and the
 * badge in the detail's publication panel. A record that reads „Koncept" in the listing and
 * „Koncept" in the editor should not change colour on the way there.
 */
const publicationTone: Record<PublicationState, string> = {
	draft: 'bg-sky-100 text-sky-800',
	scheduled: 'bg-orange/10 text-orange',
	published: 'bg-green-100 text-green-800',
}

const publicationLabel: Record<PublicationState, string> = {
	draft: 'Koncept',
	scheduled: 'Připraveno k publikaci',
	published: 'Publikováno',
}

/**
 * The grid's shape for those states — a soft rectangle, no outline. The full-round outlined pill
 * the other status columns use read as a button next to a clickable title, and the ring drew a
 * second edge around a colour that already says enough.
 */
function PublicationPill({ state }: { state: PublicationState }) {
	return (
		<span className={'inline-flex items-center whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ' + publicationTone[state]}>
			{publicationLabel[state]}
		</span>
	)
}

/**
 * „Skryto" — `SeoMeta.noIndex`. Not a publication state but a qualifier on one, so it rides
 * alongside the state pill rather than replacing it: a published article can still be kept out
 * of search. One component for both places it appears — the listing cell and the detail header —
 * so the badge does not change colour on the way between them.
 */
export function HiddenPill() {
	return (
		<span
			className="inline-flex items-center whitespace-nowrap rounded bg-orange/10 px-2 py-0.5 text-orange text-xs font-medium"
			title="Nezobrazovat ve vyhledávačích (noindex)"
		>
			Skryto
		</span>
	)
}

export function PublishedBadge({ value }: { value: boolean | null | undefined }) {
	return <PublicationPill state={value ? 'published' : 'draft'} />
}

/**
 * Cell for a grid's `isPublished` column. The column filters on the SQL view — a boolean, so a
 * scheduled record is simply „not published" there — but the row also carries `publishedAt`, the
 * view's own input, and that tells the three states apart exactly as the detail does. Only for
 * grids that select `publishedAt`; the rest keep {@link PublishedBadge}.
 *
 * Hand it the row's `seo.noIndex` ref to get a „Skryto" pill beside the state. Reading the ref is
 * also what puts the field in the grid's query — the collector proxy records every field the
 * children touch during the collection pass, and a column is the only other thing that would.
 * Omit it on entities with no SEO meta.
 */
export function renderPublishStateCell(
	noIndexField?: FieldRef<boolean>,
): (value: boolean | null, accessor: unknown) => ReactNode {
	const noIndexPath = noIndexField ? extractFieldName(noIndexField)?.split('.') ?? [] : []

	return (value, accessor) => {
		const publishedAt = (accessor as { publishedAt?: { value?: unknown } } | null)?.publishedAt?.value
		const state = typeof publishedAt === 'string' ? publicationState(publishedAt) : value ? 'published' : 'draft'

		return (
			<span className="inline-flex flex-wrap items-center gap-1.5">
				<PublicationPill state={state} />
				{readFlag(accessor, noIndexPath) ? <HiddenPill /> : null}
			</span>
		)
	}
}

/** Walks a row accessor down a selection path — `seo.noIndex` sits behind a has-one. */
function readFlag(accessor: unknown, path: readonly string[]): boolean {
	if (path.length === 0) return false

	let node: unknown = accessor
	for (const segment of path) {
		if (node == null || typeof node !== 'object') return false
		node = (node as Record<string, unknown>)[segment]
	}

	return (node as { value?: unknown } | null)?.value === true
}

export type PublicationState = 'draft' | 'scheduled' | 'published'

/**
 * `publishedAt` carries all three states: `null` is a draft, a future timestamp is queued for the
 * website to publish on its own, anything in the past is live. Same rule as `publishedFilter()`
 * on the website — keep the two in step.
 */
export function publicationState(publishedAt: string | null | undefined): PublicationState {
	const date = publishedAt ? new Date(publishedAt) : null
	if (date === null || Number.isNaN(date.getTime())) return 'draft'
	return date.getTime() > Date.now() ? 'scheduled' : 'published'
}

/** One badge for both places it shows up: the publication panel and the page header above it. */
export function PublicationStatusBadge({ publishedAt }: { publishedAt: string | null | undefined }) {
	const state = publicationState(publishedAt)
	return <span className={'rounded px-2 py-1 text-xs ' + publicationTone[state]}>{publicationLabel[state]}</span>
}

/**
 * A boolean in a grid, drawn rather than spelled: a tick in a soft green disc for
 * on, a bare light dash for off. Scanning a column of marks is faster than reading
 * a column of „Ano" — and the word survives for screen readers and on hover, which
 * is the only place the meaning would otherwise be lost. Off stays unfilled on
 * purpose: only the set rows should carry visual weight.
 */
export function BooleanMark({
	value,
	onLabel = 'Ano',
	offLabel = 'Ne',
}: {
	value: boolean | null | undefined
	onLabel?: string
	offLabel?: string
}) {
	const label = value ? onLabel : offLabel
	return (
		<span
			aria-label={label}
			className={'inline-flex size-4.5 items-center justify-center rounded-full '
				+ (value ? 'bg-green-100 text-green-600' : 'text-gray/50')}
			title={label}
		>
			{value ? <CheckIcon aria-hidden className="size-3" strokeWidth={3} /> : <span aria-hidden>–</span>}
		</span>
	)
}
